// LLM generated - Model Claude 4 Sonnet
import React, { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Textarea as MantineTextarea } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import DialogContainer from '~/domains/design-system/components/Dialog/DialogContainer';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';
import { Radio, RadioGroup } from '~/domains/design-system/components/Radio/Radio';
import { flagMessage } from '~/services/live-activity/live-activity-service.service.api';
import { ReportReason } from '~/services/live-activity/types';
import type { ReportModalProps } from '../types';
import { useStyles } from './ReportModal.styles';
import { logError } from '~/common/utils/log';

const REPORT_REASONS = [
  ReportReason.SPAM,
  ReportReason.INAPPROPRIATE_CONTENT,
  ReportReason.HATE_SPEECH,
  ReportReason.HARASSMENT,
  ReportReason.OTHER,
] as const;

export default function ReportModal({
  isOpen,
  onClose,
  contentType,
  contentId,
  reportedHandle,
  chatId,
  messageId,
  onSubmit,
  isLoading = false,
}: ReportModalProps) {
  const { t } = useTranslation('chat');
  const { classes, theme } = useStyles();
  const [selectedReason, setSelectedReason] = useState<ReportReason | ''>('');
  const [customReason, setCustomReason] = useState<string>('');

  // Mutation for flagging messages
  const flagMessageMutation = useMutation({
    mutationFn: ({ reason, description }: { reason: ReportReason; description?: string }) =>
      flagMessage(chatId, messageId, { reason, description }),
    onSuccess: () => {
      showNotification({
        title: t('notifications.reportSubmitted'),
        message: t('notifications.reportSubmittedMessage'),
        color: 'green',
        autoClose: 5000,
      });
      onClose();
    },
    onError: (error) => {
      logError('Failed to submit report:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to submit report. Please try again.',
        color: 'red',
        autoClose: 5000,
      });
    },
  });

  const handleSubmit = () => {
    if (selectedReason) {
      if (selectedReason === ReportReason.OTHER && customReason.trim()) {
        // For "other" selection, use custom reason as description
        flagMessageMutation.mutate({
          reason: ReportReason.OTHER,
          description: customReason.trim(),
        });
      } else {
        // For predefined reasons, use the reason category with empty description
        flagMessageMutation.mutate({
          reason: selectedReason as ReportReason,
          description: '',
        });
      }
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setCustomReason('');
    onClose();
  };

  const isSubmitDisabled =
    !selectedReason || (selectedReason === ReportReason.OTHER && !customReason.trim());
  const isSubmitting = flagMessageMutation.isLoading;

  return (
    <DialogContainer isOpen={isOpen} onClose={handleClose}>
      <Dialog
        type={{
          desktop: 'modal',
          mobile: 'drawer',
        }}
        isOpen={isOpen}
        onClose={handleClose}
      >
        <DialogBody>
          <div className={classes.container}>
            <div className={classes.header}>
              <Text variant="headline-xl" color={theme.colors.gray2[6]}>
                {t(`reportModal.title.${contentType}`)}
              </Text>
            </div>

            <div className={classes.content}>
              <RadioGroup
                value={selectedReason}
                onChange={(value) => setSelectedReason(value as ReportReason)}
                className={classes.radioGroup}
              >
                {REPORT_REASONS.map((reason) => (
                  <div
                    key={reason}
                    className={classes.radioItem}
                    onClick={() => setSelectedReason(reason)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedReason(reason);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <Radio
                      value={reason}
                      color={theme.colors.blue[9]}
                      aria-label={t(`reportModal.ariaLabels.selectReason`)}
                      classNames={{ radio: classes.radio, icon: classes.radioIcon }}
                    />
                    <Text
                      variant="headline-large"
                      color={theme.colors.gray2[6]}
                      className={classes.radioLabel}
                    >
                      {t(`reportModal.reasons.${reason}`)}
                    </Text>
                  </div>
                ))}
              </RadioGroup>

              {/* Show textarea when "Something else" is selected */}
              {selectedReason === ReportReason.OTHER && (
                <div className={classes.textareaContainer}>
                  <MantineTextarea
                    placeholder={t('reportModal.placeholder')}
                    value={customReason}
                    onChange={(event) => setCustomReason(event.currentTarget.value)}
                    className={classes.textarea}
                    variant="unstyled"
                    autosize
                    minRows={4}
                  />
                </div>
              )}
            </div>

            <div className={classes.buttons}>
              <Button
                variant="tertiary-inverted-fill"
                size="large"
                onClick={handleSubmit}
                disabled={isSubmitDisabled || isSubmitting}
                loading={isSubmitting}
              >
                {t('reportModal.buttons.report')}
              </Button>

              <Button
                variant="tertiary-fill"
                size="large"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('reportModal.buttons.cancel')}
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </DialogContainer>
  );
}
